/* eslint-disable no-unsafe-optional-chaining */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

interface ISupervisorTableProps {
  data: any[];
}

const AHTMonitoringTable = ({ data }: ISupervisorTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "Agent Name",
    direction: "asc",
  });

  const [searchTerm, setSearchTerm] = useState("");

  const columnHelper = createColumnHelper<any>();
  const getColumnMap = (data: any[]) => {
    if (data.length === 0) return {};
    const columnMappingList = [2, 3, 4, 14, 8, 9, 10, 11, 13, 1];
    const keys = Object.keys(data[0]);
    const columnMap: { [id: string]: string } = {};
    columnMappingList.forEach((key, index) => {
      columnMap[`Column${index + 1}`] = keys[key] || "";
    });
    console.log("columnMap", columnMap);
    return columnMap;
  };

  const getSearchableKeys = (data: any[]) => {
    if (!data.length) return [];

    const keys = Object.keys(data[0]);

    // Only columns shown in UI
    return [
      keys[2], // Agent Name
      keys[3], // File ID
      keys[4], // Logged in Time
      keys[14], // Idle Since Time
      keys[8], // Time on current Bill
      keys[9], // #Logins
      keys[10], // #Opened
      keys[11], // #Saved
      keys[13], // AHT
      keys[1], // Supervisor Name
    ].filter(Boolean);
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data) || !searchTerm.trim()) return data ?? [];

    const search = searchTerm.toLowerCase();
    const searchableKeys = getSearchableKeys(data);

    return data.filter((row) =>
      searchableKeys.some((key) =>
        String(row[key] ?? "")
          .toLowerCase()
          .includes(search)
      )
    );
  }, [data, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    const columnMap = getColumnMap(data);
    console.log("data", data);
    return [...filteredData].sort((a, b) => {
      const columnKey = columnMap[sortConfig.key];
      const aValue = a[columnKey];
      const bValue = b[columnKey];
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    if (rowsPerPage === -1) return sortedData;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage]);

  const columns = useMemo(() => {
    if (data.length === 0) return [];

    const sampleRow = data[0];
    const keys = Object.keys(sampleRow);

    return keys.slice(0, 10).map((key, index) => {
      let columnAccessor: (row: any) => any;

      switch (index) {
        case 0:
          columnAccessor = (row) => row[keys[2]];
          break;
        case 1:
          columnAccessor = (row) => row[keys[3]];
          break;
        case 2:
          columnAccessor = (row) => row[keys[4]];
          break;
        case 3:
          columnAccessor = (row) => row[keys[14]];
          break;
        case 4:
          columnAccessor = (row) => row[keys[8]];
          break;
        case 5:
          columnAccessor = (row) => row[keys[9]];
          break;
        case 6:
          columnAccessor = (row) => row[keys[10]];
          break;
        case 7:
          columnAccessor = (row) => row[keys[11]];
          break;
        case 8:
          columnAccessor = (row) => row[keys[13]];
          break;
        case 9:
          columnAccessor = (row) => row[keys[1]];
          break;
        default:
          columnAccessor = (row) => row[key];
      }

      return columnHelper.accessor(columnAccessor, {
        id: key,
        header: () => (
          <div className="p-2 flex items-center">
            <div>{getHeaderLabel(index)}</div>
            <button onClick={() => requestSort(key)} className="ml-2">
              <ArrowsUpDownIcon className="h-4 w-4" />
            </button>
          </div>
        ),
        cell: (props: any) => {
          const value = props.getValue();
          if (index === 5) {
            return (
              <div
                style={{
                  color: value > 10 ? "red" : "black",
                  fontWeight: value > 10 ? "800" : "100",
                }}
              >
                {value}
              </div>
            );
          } else if (index === 7) {
            return (
              <div
                style={{
                  color: value > 120 ? "limegreen" : "black",
                  fontWeight: value > 10 ? "800" : "100",
                }}
              >
                {value}
              </div>
            );
          } else if (index === 8) {
            return (
              <div
                style={{
                  color: value > 240 ? "red" : "black",
                  fontWeight: value > 10 ? "800" : "100",
                }}
              >
                {value === undefined ? "" : Math.round(value)}
              </div>
            );
          } else if (index === 4) {
            const row = props.row.original;
            const value6 = row[keys[6]];
            const value7 = row[keys[7]];

            if (value6 === undefined && value7 === undefined) {
              return (
                <div
                  style={{
                    color:
                      (isLessThanFourMinutes?.(value) ?? false)
                        ? "black"
                        : "red",
                  }}
                >
                  {value}
                </div>
              );
            } else {
              return <div>{""}</div>;
            }
          }

          return <div>{value}</div>;
        },
      });
    });
  }, [data, columnHelper, sortConfig]);

  function isLessThanFourMinutes(time: string): boolean {
    if (!time || !time.includes(":")) {
      return false;
    }

    const [hours, minutes, seconds]: number[] = time.split(":").map(Number);

    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
      return false;
    }

    const totalSeconds: number = hours * 3600 + minutes * 60 + seconds;
    return totalSeconds < 240;
  }

  const getHeaderLabel = (index: number) => {
    switch (index) {
      case 0:
        return "Agent Name";
      case 1:
        return "File ID";
      case 2:
        return "Logged in Time";
      case 3:
        return "Idle Since Time";
      case 4:
        return "Time on current Bill";
      case 5:
        return "#Logins";
      case 6:
        return "#Opened";
      case 7:
        return "#Saved";
      case 8:
        return "AHT";
      case 9:
        return "Supervisor Name";
      default:
        return "";
    }
  };

  const requestSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      toast.error("No data available for export.", {
        theme: "colored",
      });
      return;
    }
    const columnMap = getColumnMap(filteredData);
    const columnKeys = Object.values(columnMap);
    const formattedData = filteredData.map((row) => {
      const formattedRow: Record<string, any> = {};
      columnKeys.forEach((columnKey, index) => {
        let value = row[columnKey] ?? "";
        if (index === 8) {
          value = Math.round(Number(value));
        }
        formattedRow[getHeaderLabel(index)] = value;
      });

      return formattedRow;
    });
    console.log("formattedData", formattedData);
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AHT Monitoring");
    XLSX.writeFile(workbook, "AHTMonitoring.xlsx");
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex justify-between mb-4">
        <div>
          <label>
            Rows per page:
            <select
              value={rowsPerPage}
              onChange={(e) => {
                const value =
                  e.target.value === "all" ? -1 : parseInt(e.target.value, 10);
                setRowsPerPage(value);
                setCurrentPage(1); // Reset to the first page when rows per page changes
              }}
              className="ml-2 border border-gray-300 rounded"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={-1}>All</option>
            </select>
          </label>
        </div>
        <div>
          <label className="flex items-center">
            Search:
            <input
              type="text"
              value={searchTerm || ""}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // reset page on search
              }}
              className="ml-2 w-[140px] border border-gray-300 rounded px-2 py-1"
            />
          </label>
        </div>

        <button
          onClick={exportToExcel}
          className="bg-orange-500 text-white py-2 px-4 rounded"
        >
          Export to Excel
        </button>
      </div>
      <div className="w-full overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="p-2">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <div className="p-4">
          Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
          {Math.min(currentPage * rowsPerPage, sortedData.length)} of{" "}
          {sortedData.length} entries
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`py-1 px-3 border rounded ${
              currentPage === 1 ? "bg-gray-200 cursor-not-allowed" : "bg-white"
            }`}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {Math.ceil(sortedData.length / rowsPerPage)}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={
              currentPage === Math.ceil(sortedData.length / rowsPerPage)
            }
            className={`py-1 px-3 border rounded ${
              currentPage === Math.ceil(sortedData.length / rowsPerPage)
                ? "bg-gray-200 cursor-not-allowed"
                : "bg-white"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AHTMonitoringTable;
